import { CategoryType } from '../types/devices';

const CACHED_DEVICE_MAPPING_KEY = 'cachedDeviceMapping';

export type CachedDeviceMappingType = {
  categoryType: CategoryType;
  requestId: string;
  maxAge: number; // in seconds
  cachedAt: number; // timestamp in ms when cache was created
  userEmail: string;
};

type CachedDeviceMappingListener = () => void;

// Module-scoped singleton listeners array for testability
const cachedDeviceMappingListeners: CachedDeviceMappingListener[] = [];

export function getCachedDeviceMappingListeners(): CachedDeviceMappingListener[] {
  return cachedDeviceMappingListeners;
}

export function subscribeCachedDeviceMappingChange(
  listener: CachedDeviceMappingListener
): () => void {
  cachedDeviceMappingListeners.push(listener);
  return () => {
    const idx = cachedDeviceMappingListeners.indexOf(listener);
    if (idx > -1) cachedDeviceMappingListeners.splice(idx, 1);
  };
}

function notifyCachedDeviceMappingChange() {
  cachedDeviceMappingListeners.forEach((listener) => listener());
}

export function getCachedDeviceMapping(
  userEmail: string
): CachedDeviceMappingType[] {
  const stored = localStorage.getItem(CACHED_DEVICE_MAPPING_KEY);
  if (stored) {
    try {
      const allMappings = JSON.parse(stored) as CachedDeviceMappingType[];
      return allMappings.filter((entry) => entry.userEmail === userEmail);
    } catch {
      // fallback to empty if parsing fails
    }
  }
  return [];
}

function setCachedDeviceMapping(
  mappings: CachedDeviceMappingType[],
  userEmail: string
): void {
  const stored = localStorage.getItem(CACHED_DEVICE_MAPPING_KEY);

  let allMappings: CachedDeviceMappingType[] = [];
  if (stored) {
    try {
      allMappings = JSON.parse(stored) as CachedDeviceMappingType[];
      allMappings = allMappings.filter(
        (entry) => entry.userEmail !== userEmail
      );
      allMappings = [...allMappings, ...mappings];
    } catch {
      allMappings = mappings;
    }
  } else {
    allMappings = mappings;
  }

  localStorage.setItem(CACHED_DEVICE_MAPPING_KEY, JSON.stringify(allMappings));
  notifyCachedDeviceMappingChange();
}

/**
 * Utility to update or add a device mapping cache entry.
 * Called when GET device-mapping returns COMPLETED with max-age header.
 */
export function updateCachedDeviceMapping(
  categoryType: CategoryType,
  requestId: string,
  maxAge: number,
  userEmail: string
): void {
  const mappings = getCachedDeviceMapping(userEmail);
  const idx = mappings.findIndex(
    (entry) => entry.categoryType === categoryType
  );

  const cachedAt = Date.now();

  if (idx > -1) {
    mappings[idx] = {
      categoryType,
      requestId,
      maxAge,
      cachedAt,
      userEmail
    };
  } else {
    mappings.push({
      categoryType,
      requestId,
      maxAge,
      cachedAt,
      userEmail
    });
  }

  setCachedDeviceMapping(mappings, userEmail);
}

/**
 * Check if a valid (non-expired) cache exists for the given category.
 * Returns the cached request ID if valid, null otherwise.
 */
export function getValidCachedRequestId(
  categoryType: CategoryType,
  userEmail: string
): string | null {
  const mappings = getCachedDeviceMapping(userEmail);
  const cached = mappings.find((entry) => entry.categoryType === categoryType);

  if (!cached) {
    return null;
  }

  const now = Date.now();
  const ageInMs = now - cached.cachedAt;
  const maxAgeInMs = cached.maxAge * 1000;

  // Check if cache has expired
  if (ageInMs >= maxAgeInMs) {
    return null;
  }

  return cached.requestId;
}

/**
 * Clean up expired cache entries for a specific user.
 */
export function cleanupExpiredDeviceMappingCache(userEmail: string): void {
  const mappings = getCachedDeviceMapping(userEmail);
  const now = Date.now();

  const validMappings = mappings.filter((entry) => {
    const ageInMs = now - entry.cachedAt;
    const maxAgeInMs = entry.maxAge * 1000;
    return ageInMs < maxAgeInMs;
  });

  if (validMappings.length !== mappings.length) {
    setCachedDeviceMapping(validMappings, userEmail);
  }
}

/**
 * Clear all cached device mapping entries for a specific user.
 */
export function clearCachedDeviceMapping(userEmail: string): void {
  setCachedDeviceMapping([], userEmail);
}

/**
 * Clear cached device mapping for a specific category and user.
 */
export function clearCachedDeviceMappingForCategory(
  categoryType: CategoryType,
  userEmail: string
): void {
  const mappings = getCachedDeviceMapping(userEmail);
  const filtered = mappings.filter(
    (entry) => entry.categoryType !== categoryType
  );
  setCachedDeviceMapping(filtered, userEmail);
}
